import React from 'react';
import PropTypes from 'prop-types';

// core
import * as convert from '_core/utils/convert';
import * as api from '_core/utils/api';
import { useCartContext } from '_core/contexts/Cart';
import { getProductCustomField } from '_core/utils/getProductCustomField';
import { getValue, setValue } from '_core/utils/storage';
import { useGlobal } from '_core/hooks/useGlobal';
import { usePayment } from '_core/hooks/usePayment';
import { withLang } from '_core/hocs/withLang';
import { PaymentStepAgreement } from '_core/views/PaymentStepAgreement';
import { PaymentStepConfirm } from '_core/views/PaymentStepConfirm';
import { PaymentStepMethod } from '_core/views/PaymentStepMethod';
import { PaymentStepProduct } from '_core/views/PaymentStepProduct';
import { PaymentStepWidget } from '_core/views/PaymentStepWidget';
import { Stairs } from '_core/components/Stairs';
import { getReplaceCurrencyId } from '_core/utils/shopCurrency';


const RawPaymentSteps = (props) => {
  const {
    onClose,
    goodsData,
    document,
    getLangString: l
  } = props;
  
  const documentId = convert.toInt(document);
  
  const {
    request: { queue },
    config: { ROUTE_URL, API_METHOD, STORAGE_KEY, REPLACE_CURRENCY, REPLACE_CURRENCY_FROM_WAREHOUSE },
  } = useGlobal();

  const { processPayment } = usePayment();
  
  const cart = useCartContext();

  const maxBuys = goodsData.goods.properties.max_buys;
  const hasMaxBuys = maxBuys != null && maxBuys > 0;

  if (hasMaxBuys) {
    const data = getValue(STORAGE_KEY.GOODS) || {};
    const buys = Number.isInteger(data[goodsData.goods.id])
      ? data[goodsData.goods.id]
      : 0;

    if (buys >= maxBuys) {
      throw new Error(l('r._.shopping.payment.ban.title'));
    }
  }

  const getProductListField = name => {
    const prop = getProductCustomField(goodsData.goods, name);
    
    return Array.isArray(prop) && prop.length > 0 ? prop : null;
  };

  const childrenGoods = getProductListField('child_goods');
  const optionGoods = getProductListField('option_goods');
  const goodsCurrencyId = REPLACE_CURRENCY_FROM_WAREHOUSE
    ? getReplaceCurrencyId({
      scope: 'SHOP_GOODS',
      replaceCurrency: REPLACE_CURRENCY,
      warehouse: cart?.warehouse,
      fromWarehouse: true,
      fallback: cart?.shopCurrency?.id,
    })
    : cart?.shopCurrency?.id;

  const getGoodsPropsFunc = names => async () => {
    const result = await queue(API_METHOD.GOODS, {
      currency: goodsCurrencyId,
      names,
      hidden: true,
    });

    return result.list;
  };

  return (
    <Stairs
      goodsData={goodsData}
      initialStair={documentId ? 'payment' : null}
      stairs={[
        {
          title: l('r.vvod.summy'),
          Component: PaymentStepProduct,
          useCache: true,
          getProps:
            optionGoods != null
              ? { optionGoods: getGoodsPropsFunc(optionGoods) }
              : childrenGoods != null
              ? { childrenGoods: getGoodsPropsFunc(childrenGoods) }
              : {},
        },
        
        ...(goodsData.goods.agreement_article_list?.length ? [{
          title: l('r._.cartstep.agreement.title'),
          Component: PaymentStepAgreement,
          getProps: {
            articles: async () => await Promise.all(goodsData.goods.agreement_article_list.map((id) => api.request(API_METHOD.ARTICLE_INFO, { id })))
          },
        }] : []),
        
        {
          key: 'payment',
          title: l('r._.cartstep.payment.title'),
          Component: PaymentStepWidget,
          useCache: true,
          waitProps: {
            storage: () => {
              if (!hasMaxBuys) {
                return false;
              }
              
              const data = getValue(STORAGE_KEY.GOODS) || {};
              const buys = Number.isInteger(data[goodsData.goods.id])
                ? data[goodsData.goods.id] + 1
                : 1;
              
              data[goodsData.goods.id] = buys;
              
              setValue(STORAGE_KEY.GOODS, data);
              
              return true;
            }
          },
          getProps: {
            onClose: () => Promise.resolve(onClose),
            payment: ({ load }) => !documentId && load.goods ? (
              queue(
                API_METHOD.DOCUMENT_CREATE,
                {
                  goods: load.goods,
                  currency: goodsCurrencyId,
                },
                RawPaymentSteps.displayName
              )
            ) : Promise.resolve(null)
          }
        }
      ]}
    />
  );
};

RawPaymentSteps.displayName = 'RawPaymentSteps';

RawPaymentSteps.defaultProps = {};

RawPaymentSteps.propTypes = {
  // self props
  goodsData: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,

  // `withLang` HOC props
  langInfo: PropTypes.object,
  langStrings: PropTypes.object,
  getLangObject: PropTypes.func,
  getFirstLangString: PropTypes.func,
  getLangStringSet: PropTypes.func,
  getLangString: PropTypes.func.isRequired,
};

export const PaymentSteps = withLang(RawPaymentSteps);
